import * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import type { MapObject } from '../../models/mapObject';
import type { MarketplaceListing } from '../../models/apiTypes';
import type { ClickStartData } from './useMapClickHandlers';
import { mapSceneSingleton } from './mapScene';
import { createOnceTracker, type OnceTracker } from './analyticsOnce';

/**
 * Mutable PIXI map scene state that used to live as ~38 useRefs in MapCanvas.
 * Grouped by meaning so extracted hooks take a small group instead of a bag of refs.
 *
 * Access pattern in the component:
 *   const sceneRef = useRef(createMapSceneState());
 *   sceneRef.current.layers.mapObjects  // was mapObjectsLayerRef.current
 *
 * Hooks receive the nested group object (stable identity for the lifetime of the
 * scene ref) and mutate fields directly — no .current on the group itself.
 *
 * Seeded from mapSceneSingleton so remount after room/cityhall keeps the same
 * marker Maps / layer handles the singleton still owns.
 */

// ── Core PIXI app + viewport ──
export interface MapScenePixi {
  app: PIXI.Application | null;
  viewport: Viewport | null;
  updateViewportSize: (() => void) | null;
}

// ── Layer containers (children of viewport) ──
export interface MapSceneLayers {
  mapObjects: PIXI.Container | null;
  highlight: PIXI.Container | null;
  clouds: PIXI.Container | null;
  island: PIXI.Container | null;
}

// ── Single-node sprites / highlight containers ──
export interface MapSceneSprites {
  clouds: PIXI.Sprite | null;
  newIslandMarker: PIXI.Container | null;
  mapObjectHighlight: PIXI.Container | null;
}

// ── Texture / atlas bookkeeping ──
export interface MapSceneAssets {
  plotMarkerTexture: PIXI.Texture | null;
  decorAtlasSheet: PIXI.Spritesheet | null;
}

// ── Marker maps by entity kind ──
export interface MapSceneMarkers {
  plots: Map<number, PIXI.Container>;
  realEstate: Map<number, PIXI.Container>;
  mapObjects: Map<number, PIXI.Container>;
  quests: Map<number, PIXI.Container>;
  sales: Map<number, PIXI.Container>;
  avatars: Map<number, PIXI.Container>;
  islands: Map<number, PIXI.Container>;
}

// ── Live data mirrors (avoid stale closures in PIXI handlers) ──
export interface MapSceneData {
  mapObjects: MapObject[];
  islands: MapObject[];
  listingsByHouseId: Map<number, MarketplaceListing>;
  listedHouseIds: Set<number>;
  deletingMapObjectIds: Set<number>;
  prevMapObjectsSnapshotForIncremental: MapObject[];
}

// ── Viewport animation + render-key bookkeeping ──
export interface MapSceneViewport {
  animating: boolean;
  lastRenderKey: string | null;
}

// ── Flags mirrored from React state ──
export interface MapSceneFlags {
  godModeActive: boolean;
  showPlots: boolean;
}

// ── Admin / god-mode interaction ──
export interface MapSceneAdmin {
  clickStart: ClickStartData | null;
  objectModes: Map<number, 'dnd' | 'resize'>;
  radialCenters: Array<{ key: string; x: number; y: number }>;
  radialMarkerState: { marker: PIXI.Container; scaleX: number; scaleY: number } | null;
  selectedMarker: { marker: PIXI.Container; objectId: number } | null;
}

// ── PostHog map-load timing (once-per-session trackers + enter ts + house camera) ──
export interface MapSceneAnalytics {
  once: OnceTracker;
  mapEnterTs: number;
  didInitialHouseCamera: boolean;
}

export interface MapSceneState {
  pixi: MapScenePixi;
  layers: MapSceneLayers;
  sprites: MapSceneSprites;
  assets: MapSceneAssets;
  markers: MapSceneMarkers;
  data: MapSceneData;
  viewport: MapSceneViewport;
  flags: MapSceneFlags;
  admin: MapSceneAdmin;
  analytics: MapSceneAnalytics;
}

export function createMapSceneState(): MapSceneState {
  return {
    pixi: {
      app: mapSceneSingleton.app,
      viewport: mapSceneSingleton.viewport,
      updateViewportSize: mapSceneSingleton.updateViewportSize,
    },
    layers: {
      mapObjects: mapSceneSingleton.mapObjectsLayer,
      highlight: mapSceneSingleton.highlightLayer,
      clouds: mapSceneSingleton.cloudsLayer,
      island: mapSceneSingleton.islandLayer,
    },
    sprites: {
      clouds: mapSceneSingleton.cloudsSprite,
      newIslandMarker: mapSceneSingleton.newIslandMarker,
      mapObjectHighlight: mapSceneSingleton.mapObjectHighlight,
    },
    assets: {
      // Seed from singleton; SceneManager also writes here when plot.webp loads.
      // Consumers must prefer mapSceneSingleton.plotMarkerTexture at draw time
      // (live value) — this field alone can stay null if state was created first.
      plotMarkerTexture: mapSceneSingleton.plotMarkerTexture,
      decorAtlasSheet: null,
    },
    markers: {
      // Share Map instances with the singleton so room round-trips keep markers.
      plots: mapSceneSingleton.plotMarkers,
      realEstate: mapSceneSingleton.realEstateMarkers,
      mapObjects: mapSceneSingleton.mapObjectMarkers,
      quests: mapSceneSingleton.questMarkers,
      // Component-local maps (not on singleton) — same as former useRef(new Map()).
      sales: new Map(),
      avatars: new Map(),
      islands: mapSceneSingleton.islandMarkers,
    },
    data: {
      mapObjects: [],
      islands: [],
      listingsByHouseId: new Map(),
      listedHouseIds: new Set(),
      deletingMapObjectIds: new Set(),
      prevMapObjectsSnapshotForIncremental: [],
    },
    viewport: {
      animating: false,
      lastRenderKey: mapSceneSingleton.lastMapObjectsRenderKey,
    },
    flags: {
      godModeActive: false,
      showPlots: false,
    },
    admin: {
      clickStart: null,
      objectModes: new Map(),
      radialCenters: [],
      radialMarkerState: null,
      selectedMarker: null,
    },
    analytics: {
      once: createOnceTracker(),
      mapEnterTs: 0,
      didInitialHouseCamera: false,
    },
  };
}
